import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useStores } from '../hooks/useStores';
import { useLocations } from '../hooks/useAnalytics';
import { glassStyle } from '../theme/luxuryTheme';
import EnhancedLocationSelector from '../components/EnhancedLocationSelector';
import { hybridApi, salesApi } from '../services/api';
import { format, subDays } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const StoreComparison: React.FC = () => {
  const theme = useTheme();
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [comparisonMetric, setComparisonMetric] = useState<string>('sales');
  const [storeData, setStoreData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');

  const handleMetricChange = (event: SelectChangeEvent) => {
    setComparisonMetric(event.target.value);
  };

  const handleStoreSelection = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedStores(value);
    } else {
      setSelectedStores([value]);
    }
  };

  // Fetch sales data for selected stores using batch endpoint
  useEffect(() => {
    const fetchStoreData = async () => {
      if (selectedStores.length === 0) {
        setStoreData([]);
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        // Use batch endpoint for better performance
        const response = await hybridApi.getStoresComparison(selectedStores, yesterday);
        const data = response.data;

        // Ensure stores is an array
        const stores = Array.isArray(data.stores) ? data.stores : [];

        // Transform the data for display
        const transformedData = stores.map((store: any) => ({
          id: store.storeId || store.id,
          name: store.storeName || store.name,
          sales: store.dailySales?.totalSales || store.daily?.sales || 0,
          orders: store.dailySales?.orderCount || store.daily?.orders || 0,
          avgOrder: store.dailySales?.averageOrderValue || store.daily?.avgOrder || 0,
          mtdSales: store.mtdSales?.totalSales || store.mtd?.sales || 0,
          ytdSales: store.ytdSales?.totalSales || store.ytd?.sales || 0,
          brand: store.brand,
          city: store.city,
          state: store.state
        }));
        
        setStoreData(transformedData);
      } catch (err) {
        console.error('Error fetching store comparison data:', err);
        setError('Failed to load store comparison data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoreData();
  }, [selectedStores, yesterday]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const getMetricValue = (store: any) => {
    switch (comparisonMetric) {
      case 'sales': return store.sales;
      case 'orders': return store.orders;
      case 'avgOrder': return store.avgOrder;
      default: return store.sales;
    }
  };

  const getMetricLabel = () => {
    switch (comparisonMetric) {
      case 'sales': return 'Total Sales ($)';
      case 'orders': return 'Number of Orders';
      case 'avgOrder': return 'Average Order Value ($)';
      default: return 'Total Sales ($)';
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
        Store Comparison
      </Typography>

      {/* Store Selection */}
      <Paper sx={{ ...glassStyle, p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>Select Stores to Compare</Typography>
            <EnhancedLocationSelector
              value={selectedStores}
              onChange={handleStoreSelection}
              multiple={true}
              label="Select Stores"
              showSearch={true}
              showBrandFilter={true}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Comparison Metric</InputLabel>
              <Select
                value={comparisonMetric}
                label="Comparison Metric"
                onChange={handleMetricChange}
              >
                <MenuItem value="sales">Total Sales</MenuItem>
                <MenuItem value="orders">Number of Orders</MenuItem>
                <MenuItem value="avgOrder">Average Order Value</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selected Stores
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {storeData.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Combined Sales
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                ${storeData.reduce((sum, store) => sum + store.sales, 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Top Performer
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {storeData.length > 0 ? storeData.reduce((max, store) => 
                  store.sales > max.sales ? store : max, storeData[0]
                )?.name : 'N/A'}
              </Typography>
              <Chip label="Highest Sales" size="small" color="success" sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={glassStyle}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Average per Store
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                ${storeData.length > 0 ? (storeData.reduce((sum, store) => sum + store.sales, 0) / storeData.length).toFixed(0) : '0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Comparison Chart */}
      {storeData.length > 0 ? (
        <Paper sx={{ ...glassStyle, p: 3, height: 500 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Store Performance Comparison
          </Typography>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={storeData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45}
              textAnchor="end"
              height={100}
            />
            <YAxis 
              label={{ 
                value: getMetricLabel(), 
                angle: -90, 
                position: 'insideLeft' 
              }}
            />
            <Tooltip 
              formatter={(value: number) => 
                comparisonMetric === 'avgOrder' 
                  ? `$${value.toFixed(2)}` 
                  : comparisonMetric === 'sales'
                  ? `$${value.toLocaleString()}`
                  : value.toLocaleString()
              }
            />
            <Bar dataKey={comparisonMetric} fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Paper>
      ) : (
        <Paper sx={{ ...glassStyle, p: 3, height: 200 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography variant="body1" color="text.secondary">
              Select stores to see comparison chart
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Store Details Table */}
      {storeData.length > 0 && (
        <Paper sx={{ ...glassStyle, p: 3, mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Store Details
          </Typography>
          <Grid container spacing={2}>
            {storeData.map((store) => (
              <Grid item xs={12} md={6} lg={4} key={store.id}>
                <Card sx={{ ...glassStyle, border: `1px solid ${theme.palette.divider}` }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {store.name}
                    </Typography>
                    <Chip label={`ID: ${store.id}`} size="small" sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Sales: ${store.sales.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Orders: {store.orders}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Order: ${store.avgOrder.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default StoreComparison;